<script setup>
import { computed, ref, watch, getCurrentInstance } from 'vue';
import { useForm } from '@inertiajs/vue3';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    selectedBankAccount: {
        type: Object,
        default: () => null
    },
    invoiceData: {
        type: Object,
        default: () => ({})
    },
    isEditMode: {
        type: Boolean,
        default: false
    },
    userType: {
        type: String,
        default: 'admin',
        validator: (value) => ['admin', 'client'].includes(value)
    },
    isInvoiceMode: {
        type: Boolean,
        default: false
    },
    paymentLinkValue: {
        type: String,
        default: ''
    },
    canEdit: {
        type: Boolean,
        default: true
    },
    showPaymentLinkField: {
        type: Boolean,
        default: true
    },
    showNoPaymentLinkOption: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits([
    'update:paymentLinkValue',
    'paymentLinkOption:update',
    'customPaymentLink:update'
]);

const paymentLinkOption = ref('bank_account');
const customPaymentLink = ref('');
const paymentLinkError = ref('');

watch(() => props.paymentLinkValue, (newValue) => {
    if (newValue) {
        if (props.selectedBankAccount?.payment_link === newValue) {
            if (paymentLinkOption.value !== 'custom' && paymentLinkOption.value !== 'none') {
                paymentLinkOption.value = 'bank_account';
            }
            if (paymentLinkOption.value === 'bank_account') {
                customPaymentLink.value = '';
            }
        } else {
            paymentLinkOption.value = 'custom';
            customPaymentLink.value = newValue;
        }
    } else {
        if (paymentLinkOption.value !== 'custom' && paymentLinkOption.value !== 'none') {
            paymentLinkOption.value = props.selectedBankAccount?.payment_link ? 'bank_account' : 'none';
        }
        if (paymentLinkOption.value !== 'custom') {
            customPaymentLink.value = '';
        }
    }
}, { immediate: true });

watch(() => props.selectedBankAccount, (newBankAccount, oldBankAccount) => {
    if (newBankAccount?.id !== oldBankAccount?.id) {
        if (!props.paymentLinkValue) {
            if (newBankAccount?.payment_link) {
                paymentLinkOption.value = 'bank_account';
                customPaymentLink.value = '';
                emit('update:paymentLinkValue', newBankAccount.payment_link);
            } else {
                if (paymentLinkOption.value === 'bank_account') {
                    paymentLinkOption.value = 'none';
                    customPaymentLink.value = '';
                    emit('update:paymentLinkValue', '');
                }
            }
        } else {
            if (newBankAccount?.payment_link === props.paymentLinkValue) {
                if (paymentLinkOption.value !== 'custom') {
                    paymentLinkOption.value = 'bank_account';
                    customPaymentLink.value = '';
                }
            } else if (paymentLinkOption.value === 'bank_account' && newBankAccount?.payment_link !== props.paymentLinkValue) {
                paymentLinkOption.value = 'custom';
                customPaymentLink.value = props.paymentLinkValue;
            }
        }
    }
}, { deep: true });

const validatePaymentLink = (url) => {
    if (!url || url.trim() === '') {
        return { isValid: false, error: $t('Payment link is required') };
    }
    
    url = url.trim();
    
    try {
        const urlObj = new URL(url);
        
        if (urlObj.protocol !== 'https:') {
            return { 
                isValid: false, 
                error: $t('Payment link must use HTTPS protocol') 
            };
        }
        
        const dangerousSchemes = ['javascript:', 'data:', 'file:', 'ftp:'];
        if (dangerousSchemes.some(scheme => url.toLowerCase().startsWith(scheme))) {
            return { 
                isValid: false, 
                error: $t('Invalid payment link format') 
            };
        }
        
        if (!urlObj.hostname || urlObj.hostname.length < 3) {
            return { 
                isValid: false, 
                error: $t('Please enter a valid domain') 
            };
        }
        
        return { isValid: true, error: '' };
    } catch (error) {
        return { 
            isValid: false, 
            error: $t('Please enter a valid URL starting with https://') 
        };
    }
};

watch(customPaymentLink, (newValue) => {
    if (paymentLinkOption.value === 'custom') {
        if (!newValue || newValue.trim() === '') {
            paymentLinkError.value = $t('Custom payment link is required');
            emit('update:paymentLinkValue', '');
        } else {
            const validation = validatePaymentLink(newValue);
            paymentLinkError.value = validation.error;
            
            if (validation.isValid) {
                emit('update:paymentLinkValue', newValue.trim());
            } else {
                emit('update:paymentLinkValue', '');
            }
        }
        emit('customPaymentLink:update', newValue);
    }
});

watch(paymentLinkOption, (newOption) => {
    paymentLinkError.value = '';
    
    switch (newOption) {
        case 'bank_account':
            const bankLink = props.selectedBankAccount?.payment_link || '';
            customPaymentLink.value = '';
            emit('update:paymentLinkValue', bankLink);
            break;
        case 'custom':
            if (!customPaymentLink.value || customPaymentLink.value.trim() === '') {
                paymentLinkError.value = $t('Custom payment link is required');
                emit('update:paymentLinkValue', '');
            } else {
                const validation = validatePaymentLink(customPaymentLink.value);
                paymentLinkError.value = validation.error;
                if (validation.isValid) {
                    emit('update:paymentLinkValue', customPaymentLink.value.trim());
                } else {
                    emit('update:paymentLinkValue', '');
                }
            }
            break;
        case 'none':
            customPaymentLink.value = '';
            emit('update:paymentLinkValue', '');
            break;
    }
    
    emit('paymentLinkOption:update', newOption);
});

const finalPaymentLink = computed(() => {
    switch (paymentLinkOption.value) {
        case 'bank_account':
            return props.selectedBankAccount?.payment_link || '';
        case 'custom':
            return customPaymentLink.value || '';
        case 'none':
        default:
            return '';
    }
});

const isPaymentLinkValid = computed(() => {
    if (paymentLinkOption.value === 'none') {
        return true;
    }
    
    if (paymentLinkOption.value === 'bank_account') {
        return !!props.selectedBankAccount?.payment_link;
    }
    
    if (paymentLinkOption.value === 'custom') {
        if (!customPaymentLink.value || customPaymentLink.value.trim() === '') {
            return false;
        }
        return validatePaymentLink(customPaymentLink.value).isValid;
    }
    
    return false;
});

const validateForSending = () => {
    if (paymentLinkOption.value === 'custom') {
        if (!customPaymentLink.value || customPaymentLink.value.trim() === '') {
            paymentLinkError.value = $t('Custom payment link is required');
            return false;
        }
        const validation = validatePaymentLink(customPaymentLink.value);
        paymentLinkError.value = validation.error;
        return validation.isValid;
    }
    
    if (paymentLinkOption.value === 'bank_account') {
        if (!props.selectedBankAccount?.payment_link) {
            return false;
        }
    }
    
    return true;
};

defineExpose({
    validateForSending,
    isPaymentLinkValid,
    finalPaymentLink
});
</script>

<template>
    <div :class="[
        'bg-surface-50 dark:bg-surface-800 rounded-lg',
        userType === 'client' ? 'p-3 sm:p-4' : 'p-4',
        isEditMode && userType === 'admin' ? 'dark:bg-surface-800 border' : ''
    ]">
        <h3 :class="[
            'font-semibold',
            userType === 'client' ? 'text-base sm:text-lg mb-2 sm:mb-3' : 'text-lg mb-3',
            isEditMode && userType === 'admin' ? 'text-yellow-800' : ''
        ]">{{ $t('Payment Information') }}</h3>

        <div v-if="isEditMode && userType === 'admin' && canEdit" class="space-y-4">
            <!-- Bank/Payment Method (commented out in original) -->
            <!-- <div>
                <label class="block text-sm font-medium text-surface-700 mb-2">{{ $t('Bank/Payment Method') }}</label>
                <InputText
                    v-model="invoiceData.bank"
                    class="w-full"
                    :placeholder="$t('Enter bank or payment method')"
                />
            </div> -->

            <div v-if="showPaymentLinkField">
                <label class="block text-sm font-medium text-surface-700 mb-3">{{ $t('Payment Link Options') }}</label>
                <div class="space-y-3">
                    <!-- Bank Account Payment Link -->
                    <div class="flex items-center">
                        <RadioButton
                            v-model="paymentLinkOption"
                            inputId="bank_account"
                            value="bank_account"
                            :disabled="!selectedBankAccount?.payment_link"
                        />
                        <label for="bank_account" class="ml-2 text-sm" :class="!selectedBankAccount?.payment_link ? 'text-surface-500' : ''">
                            {{ $t('Use selected bank account payment link') }}
                            <span v-if="!selectedBankAccount?.payment_link" class="text-xs text-surface-400 block">
                                ({{ $t('No payment link available for selected bank account') }})
                            </span>
                            <span v-else-if="selectedBankAccount?.payment_link" class="text-xs text-green-600 block">
                                {{ selectedBankAccount.payment_link }}
                            </span>
                        </label>
                    </div>

                    <!-- Custom Payment Link -->
                    <div class="flex items-center">
                        <RadioButton
                            v-model="paymentLinkOption"
                            inputId="custom"
                            value="custom"
                        />
                        <label for="custom" class="ml-2 text-sm">
                            {{ $t('Use custom payment link') }}
                        </label>
                    </div>

                    <!-- Custom Link Input -->
                    <div v-if="paymentLinkOption === 'custom'" class="ml-6">
                        <InputText
                            v-model="customPaymentLink"
                            class="w-full"
                            type="url"
                            :class="{ 'p-invalid': paymentLinkError }"
                            :placeholder="$t('Enter custom payment link (https://...)')"
                        />
                        <small v-if="paymentLinkError" class="p-error block mt-1">
                            {{ paymentLinkError }}
                        </small>
                    </div>

                    <!-- No Payment Link -->
                    <div v-if="showNoPaymentLinkOption" class="flex items-center">
                        <RadioButton
                            v-model="paymentLinkOption"
                            inputId="none"
                            value="none"
                        />
                        <label for="none" class="ml-2 text-sm">
                            {{ $t('No payment link (invoice will be sent without payment link)') }}
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <!-- Read-only view -->
        <div v-else class="space-y-2">
            <div>
                <span :class="[
                    'font-medium text-surface-600',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ $t('Bank') }}:</span>
                <span :class="[
                    'ml-2 text-surface-900',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ selectedBankAccount?.name || invoiceData.bank || 'WISE' }}</span>
            </div>
            <div v-if="selectedBankAccount?.bank_name">
                <span :class="[
                    'font-medium text-surface-600',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ $t('Bank Name') }}:</span>
                <span :class="[
                    'ml-2 text-surface-900',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ selectedBankAccount.bank_name }}</span>
            </div>
            <div v-if="selectedBankAccount?.account_number">
                <span :class="[
                    'font-medium text-surface-600',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ $t('Account Number') }}:</span>
                <span :class="[
                    'ml-2 text-surface-900',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ selectedBankAccount.account_number }}</span>
            </div>
            <div v-if="selectedBankAccount?.iban">
                <span :class="[
                    'font-medium text-surface-600',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ $t('IBAN') }}:</span>
                <span :class="[
                    'ml-2 text-surface-900',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ selectedBankAccount.iban }}</span>
            </div>
            <div v-if="selectedBankAccount?.bic_swift">
                <span :class="[
                    'font-medium text-surface-600',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ $t('SWIFT/BIC') }}:</span>
                <span :class="[
                    'ml-2 text-surface-900',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ selectedBankAccount.bic_swift }}</span>
            </div>
            <div v-if="finalPaymentLink">
                <span :class="[
                    'font-medium text-surface-600',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ $t('Payment Link') }}:</span>
                <a
                    :href="finalPaymentLink"
                    target="_blank"
                    :class="[
                        'ml-2 text-blue-600 hover:underline break-all',
                        userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                    ]"
                >
                    {{ finalPaymentLink }}
                    <i class="pi pi-external-link ml-1 text-xs"></i>
                </a>
            </div>
            <div v-if="isInvoiceMode && invoiceData.number">
                <span :class="[
                    'font-medium text-surface-600',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">{{ $t('Payment Reason') }}:</span>
                <span :class="[
                    'ml-2 text-surface-900',
                    userType === 'client' ? 'text-xs sm:text-sm' : 'text-sm'
                ]">Invoice #{{ invoiceData.number }}</span>
            </div>
        </div>
    </div>
</template>